// src/components/HandoverPanel.js
//
// WO-001 S6 (2026-09-14, task list section 243). Design SS4, the planned handover: two-party, neither
// side alone. Two faces of one record, handovers/{id}, written only by the Cloud Functions:
//   <HandoverInbox user />           what is proposed TO me (accept / decline) and what I have proposed
//                                    (withdraw). On the Dashboard, because a successor may hold no
//                                    desk yet.
//   <ProposeHandover user office term />   on a held row of the seats panel, for the sitting holder
//                                    (or the administrator): successor's account, effective date, method.
// The decisions are src\data\officeSuccession.js's, inside the functions; here we draw and call.
import React, { useEffect, useState } from 'react';
import { db } from '../firebase';
import { collection, query, where, onSnapshot } from 'firebase/firestore';
import { getFunctions, httpsCallable } from 'firebase/functions';

const C = { panel: '#0B1626', line: '#2E4A6B', dim: '#8AA4BF', accent: '#E8A020', bad: '#E06C6C', ok: '#5FB878', text: '#DCE7F2' };
const call = async (name, payload) => { const fn = httpsCallable(getFunctions(), name); const r = await fn(payload); return r.data; };

// Ruling 304 (audit F40 (b)): a General Chairperson's office changes hands only after the administrator acknowledges.
const awaitsAdmin = (h) => h.officeType === 'general_committee' && !h.adminAcknowledgedAt;

function Row({ h, me, isAdmin, onDone }) {
  const [busy, setBusy] = useState(false);
  const [msg, setMsg] = useState('');
  const mine = h.proposerUid === me;
  const successor = h.successorUid === me;
  const act = async (name, payload, okText) => {
    setBusy(true); setMsg('');
    try { const r = await call(name, payload); setMsg(okText(r)); if (onDone) onDone(); }
    catch (e) { setMsg('Refused: ' + (e && e.message ? e.message : String(e))); }
    setBusy(false);
  };
  return (
    <div style={{ borderTop: '1px solid ' + C.line, padding: '8px 0', fontSize: 12, color: C.dim }}>
      <div style={{ color: C.text, fontWeight: 700 }}>{h.officeId} · {mine ? 'to ' + h.successorName : 'from ' + h.proposerName} · effective {h.effectiveOn} · {h.method}</div>
      <div>{h.status.toUpperCase()} · proposed {String(h.proposedAt || '').slice(0, 10)}{h.note ? ' · ' + h.note : ''}{h.resultTermId ? ' · term ' + h.resultTermId : ''}</div>
      {h.officeType === 'general_committee' ? (
        <div style={{ color: h.adminAcknowledgedAt ? C.ok : C.accent }}>
          {h.adminAcknowledgedAt ? 'Acknowledged by the administrator ' + String(h.adminAcknowledgedAt).slice(0, 10) : 'Waiting on the administrator\'s acknowledgement - a General Chairperson\'s office changes hands only after it.'}
        </div>
      ) : null}
      {h.status === 'proposed' && isAdmin && awaitsAdmin(h) ? (
        <button className="btn btn-primary btn-sm" style={{ marginTop: 6 }} disabled={busy} onClick={() => act('decideHandover', { handoverId: h.id, decision: 'acknowledged' }, () => 'Acknowledged. ' + h.successorName + ' may now accept.')}>Acknowledge this handover</button>
      ) : null}
      {h.status === 'proposed' && successor ? (
        <div style={{ marginTop: 6, display: 'flex', gap: 8 }}>
          <button className="btn btn-primary btn-sm" disabled={busy || awaitsAdmin(h)} onClick={() => act('acceptHandover', { handoverId: h.id }, r => 'Accepted. You hold ' + h.officeId + ' from ' + h.effectiveOn + ' (term ' + r.termId + '). Sign out and in to carry the new token.')}>Accept - I take up {h.officeId} on {h.effectiveOn}</button>
          <button className="btn btn-secondary btn-sm" disabled={busy} onClick={() => act('decideHandover', { handoverId: h.id, decision: 'declined' }, () => 'Declined.')}>Decline</button>
        </div>
      ) : null}
      {h.status === 'proposed' && mine ? <button className="btn btn-secondary btn-sm" style={{ marginTop: 6 }} disabled={busy} onClick={() => act('decideHandover', { handoverId: h.id, decision: 'withdrawn' }, () => 'Withdrawn.')}>Withdraw</button> : null}
      {msg ? <div style={{ color: /^Refused/.test(msg) ? C.bad : C.ok, marginTop: 4 }}>{msg}</div> : null}
    </div>
  );
}

export function HandoverInbox({ user }) {
  const [rows, setRows] = useState(null);
  const [err, setErr] = useState('');
  useEffect(() => {
    if (!user || !user.uid) return undefined;
    const qs = [query(collection(db, 'handovers'), where('successorUid', '==', user.uid)), query(collection(db, 'handovers'), where('proposerUid', '==', user.uid))];
    // Ruling 304: the administrator also sees every open General Chairperson handover, to acknowledge it.
    if (user.isAdmin) qs.push(query(collection(db, 'handovers'), where('officeType', '==', 'general_committee'), where('status', '==', 'proposed')));
    const per = qs.map(() => null);
    const merge = () => { if (per.every(x => x)) { const m = new Map(); per.flat().forEach(r => m.set(r.id, r)); setRows(Array.from(m.values()).sort((a, b) => String(b.proposedAt) < String(a.proposedAt) ? -1 : 1)); } };
    const unsubs = qs.map((q, i) => onSnapshot(q, s => { per[i] = s.docs.map(d => ({ id: d.id, ...d.data() })); merge(); }, e => { setErr('Handovers could not be read: ' + (e.code || e.message)); setRows([]); }));
    return () => unsubs.forEach(u => u());
  }, [user && user.uid, user && user.isAdmin]);
  if (!user || rows === null || (rows.length === 0 && !err)) return null;   // nothing to show, nothing shown
  const open = rows.filter(h => h.status === 'proposed');
  return (
    <div style={{ border: '1px solid ' + (open.length ? C.accent : C.line), background: C.panel, marginBottom: 14 }}>
      <div style={{ padding: '10px 14px', borderBottom: '1px solid ' + C.line }}>
        <div style={{ fontSize: 13, fontWeight: 700, color: C.text }}>Handover of office</div>
        <div style={{ fontSize: 11, color: C.dim, marginTop: 2 }}>Two-party: the sitting holder proposes, the successor accepts; on accept one act closes the outgoing term, opens the new one and rewrites both tokens. Neither side can complete it alone. A General Chairperson's office also needs the administrator's acknowledgement first.</div>
      </div>
      <div style={{ padding: '4px 14px 10px' }}>
        {err ? <div style={{ color: C.bad, fontSize: 12 }}>&#9888; {err}</div> : null}
        {rows.map(h => <Row key={h.id} h={h} me={user.uid} isAdmin={!!user.isAdmin} />)}
      </div>
    </div>
  );
}

export function ProposeHandover({ user, office, term }) {
  const [form, setForm] = useState({ successorEmail: '', successorName: '', effectiveOn: '', method: 'elected', note: '' });
  const [open, setOpen] = useState(false);
  const [busy, setBusy] = useState(false);
  const [msg, setMsg] = useState('');
  if (!user || !office || !term || term.endedOn) return null;
  if (!(user.isAdmin || term.holderUid === user.uid)) return null;
  const propose = async () => {
    setBusy(true); setMsg('');
    try { const r = await call('proposeHandover', { officeId: office.officeId, ...form }); setMsg('Proposed (' + r.handoverId + '). ' + form.successorEmail + ' sees it on his Dashboard and accepts or declines.'); setOpen(false); }
    catch (e) { setMsg('Refused: ' + (e && e.message ? e.message : String(e))); }
    setBusy(false);
  };
  return (
    <div style={{ marginTop: 6 }}>
      {!open ? <button className="btn btn-secondary btn-sm" onClick={() => setOpen(true)}>{term.holderUid === user.uid ? 'Hand off this office' : 'Propose a handover'}</button> : (
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 8, padding: 8, border: '1px solid ' + C.accent }}>
          <input aria-label="Successor" className="form-input" placeholder="Successor's account e-mail" value={form.successorEmail} onChange={e => setForm({ ...form, successorEmail: e.target.value })} />
          <input aria-label="Successor" className="form-input" placeholder="Successor's name" value={form.successorName} onChange={e => setForm({ ...form, successorName: e.target.value })} />
          <input aria-label="Effective on" className="form-input" type="date" value={form.effectiveOn} onChange={e => setForm({ ...form, effectiveOn: e.target.value })} title="The day the successor takes up the office" />
          <select aria-label="How the office changed hands" className="form-select" value={form.method} onChange={e => setForm({ ...form, method: e.target.value })}><option value="elected">elected</option><option value="appointed">appointed</option></select>
          <input aria-label="Note (election, convention, letter; optional)" className="form-input" style={{ gridColumn: '1 / span 2' }} placeholder="Note (election, convention, letter; optional)" value={form.note} onChange={e => setForm({ ...form, note: e.target.value })} />
          <button className="btn btn-primary btn-sm" disabled={busy || !form.successorEmail || !form.effectiveOn} onClick={propose}>Propose</button>
          <button className="btn btn-secondary btn-sm" disabled={busy} onClick={() => setOpen(false)}>Cancel</button>
        </div>
      )}
      {msg ? <div style={{ fontSize: 12, color: /^Refused/.test(msg) ? C.bad : C.ok, marginTop: 4 }}>{msg}</div> : null}
    </div>
  );
}
